/**
 * Handles all modals
 * @module modal
 */

import $ from 'jquery';
import $script from 'scriptjs';
import { get } from '../lib/props';
import _get from 'lodash/get';
import Keyboard from './keyboard';
import registerJQueryPlugin, { noopActions } from '../lib/register-jquery-plugin';

const HAMMERJS_FALLBACK = 'https://ajax.googleapis.com/ajax/libs/hammerjs/2.0.8/hammer.min.js';

/**
 * Makes a request for HammerJS.
 * @private
 * @return {Promise} Denotes when the request has been successfully executed
 */
const loadHammerJSFunc = () =>
  new Promise((resolve) => {
    $script(get('scripts.hammerJS', HAMMERJS_FALLBACK), () => {
      resolve(window.Hammer);
    });
  });


// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'modal';
export const DEFAULTS = {
  url: '',
  type: '',
  open: null,
  data: '',
  animation: true,
  returnFocus: '',
  notificationAutoClose: true,
  notificationDuration: 5000,
  cache: true,
  closeButton: true,
  overlayClose: true,
  triggerOpen: false,
  triggerElement: '',
  setA11yOnly: false,
  padding: true,
  video: false,
  portrait: false,
  fullHeight: false,
  windowHeight: false,
  transparent: false,
  showProgressBar: true,
  scrolling: true,
  triggerByClick: true, // Use `false` when needed to trigger a modal and have swipe functionality, ex, pdp product 2 column
  loadHammerJS: loadHammerJSFunc,
  onOpen: () => {},
  onComplete: () => {},
  onLoad: () => {},
  onCleanup: () => {},
  onClose: () => {},
  onSuccess: (data) => {},
  onPrepend: () => {},
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
  srLinkText: get('rcProps.translations.openModalSRText', 'Opens in a modal window'),
};

/**
 * Check if options.url is define, if it is create an empty modal
 * for ajax content. Otherwise, assign with options.open or $el's data attribute
 * @return {jQuery} modal element
 */
const appendModal = () => {
  const $modalHTML = $('<div/>', { id: 'modal', class: 'modal' });
  return $modalHTML;
};
const hasHash = ($el) => {
  const href = $el.attr('href');
  if (href) {
    // Double check this logic not sure if this is working correctly - maurice
    if (href.toLowerCase().indexOf('#') === -1) {
      return href;
    }
  }
  return false;
};

/**
 * Set Aria
 * @param  {jQuery} $el - button /element that opens modal
 */
 const setA11yAttr = ($el, options = {}) => {
  $el
    .attr('aria-haspopup', 'dialog')
    if ($el.is('a')) {
      $el.attr('role', 'button');
    } else if ($el.is('button')) {
      $el.attr('type', 'button');
    }
};

/**
 * Get Modal
 * Target modal via options.open / data-open(legacy) / href(accessibility)
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal element
 */
const getModal = ($el, options = {}) => {
  let $modal;
  if (options.open) {
    $modal = $(`#${options.open}`);
  } else if ($el.attr('href') && !hasHash($el)) {
    $modal = $($el.attr('href'));
  } else if (options.url || $el.data('url') || hasHash($el)) {
    if ($('#modal').length) {
      $modal = $('#modal');
    } else {
      $modal = appendModal();
    }
  } else if ($el.data('open')) {
    $modal = $(`#${$el.data('open')}`);
  }
  return $modal;
};
/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  // Assign $modal to modal element either by options {} or $el's data attribute
  const $modal = getModal($el, options);
  let opts = {};
  const modalData = !$modal ? {} : $modal.data();
  if (!$modal && !options.setA11yOnly) return false;
  opts = Object.assign({}, DEFAULTS, $el.data(), options);
  if (!options.url) {
    opts = Object.assign({}, DEFAULTS, modalData, $el.data(), options);
  }
  if (hasHash($el)) {
    opts.url = hasHash($el);
  }
  return opts;
};

/**
 * Check if options.url is define, if it is create an empty modal
 * otherwise target options.open or $el's data attribute
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal element
 */
const setModal = ($el, options = {}) => {
  // Assign $modal to modal element either by options {} or $el's data attribute
  const $modal = getModal($el, options);
  $modal.appendTo('body');
  return $modal;
};
/**
 * @param  {jQuery} $overlay - modal overlay
 */
const appendLoadingBar = ($overlay) => {
  if (!$('.modal-overlay__progress-bar').length) {
    let ajaxLoadingMessage;
    try {
      ajaxLoadingMessage = window.rcProps.translations.ajaxLoadingMessage;
    } catch (e) {
      ajaxLoadingMessage = 'Content is loading';
    }
    $(`<div class="modal-overlay__progress-bar modal-overlay__progress-bar--hidden progress-bar progress-bar--center" tabindex="-1">
        <div class="u-screen-reader">
            ${ajaxLoadingMessage}
        </div>
     </div>`).appendTo($overlay);
  }
};
/**
 * @return {jQuery} modal overlay element
 */
const appendModalOverlay = (options) => {
  if (!$('.modal-overlay').length) {
    $('<div class="modal-overlay"></div>').appendTo('body');
  }
  if (!options.animation) {
    $('.modal-overlay').addClass('modal-overlay--no-animation');
  }
  return $('.modal-overlay');
};
/**
 * Check if options.url is define, if it is create an empty modal
 * otherwise target options.open or $el's data attribute
 * @param  {jQuery} $el - modal element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal content element
 */
const wrapModalContent = ($el, options = {}) => {
  if (!$el.find('.modal__content').length) {
    $el.wrapInner('<div role="dialog" tabindex="-1" class="modal__content"></div>');
  }
  if (!options.padding) {
    $el.find('.modal__content').addClass('u-padding-a--none');
  }
  if (!options.animation) {
    $el.find('.modal__content').addClass('modal__content--no-animation');
  }
  if (options.video) {
    $el.find('.modal__content').addClass('modal__content--video');
  }
  if (options.portrait) {
    $el.find('.modal__content').addClass('modal__content--video__portrait');
  }
  if (options.fullHeight) {
    $el.find('.modal__content').addClass('modal__content--full-height');
  }
  if (!options.scrolling) {
    $el.find('.modal__content').addClass('u-overflow--hidden');
  }

  // used when you need to have the modal full window height
  if (options.windowHeight) {
    $el.find('.modal__content').addClass('u-height--full');
    setTimeout(()=>{
      $('#modal').addClass('u-padding-a--none');
    });
  }
  return $el.find('.modal__content');
};
/**
 * Adjust and set classes and options for appropriate modal type
 * @param  {jQuery} $el - modal element
 * @param  {string} type - modifier
 */
const addModalModifiers = ($el, type) => {
  const modalTypeClass = `modal--${type}`;
  if (type) {
    $el.addClass(modalTypeClass);
  }
};
/**
 * Adjust and set classes and options for appropriate modal type
 * @param  {jQuery} $el - modal element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable modal settings
 */
const adjustToModalType = ($el, options = {}) => {
  let typeAdjust = {
    pageScroll: false,
    overlay: true,
  };
  addModalModifiers($el, options.type);
  switch (options.type) {
    case 'notification':
      typeAdjust = {
        pageScroll: true,
        overlay: false,
      };
      break;
    case 'notification-bubble':
      typeAdjust = {
        pageScroll: true,
        overlay: false,
      };
      break;
    default:
  }
  return typeAdjust;
};
/**
 * Remove active class on elements
 * @param  {rest}
 */
const removeActiveClass = (...args) => {
  args.forEach(($el) => {
    $el.removeClass('is-active');
  });
};
/**
 * @param  {jQuery} $el - modal element
 */
const removeModalModifiers = ($el) => {
  const classArray = Array.from($el[0].classList);
  const modifers = classArray.filter(modalClass => modalClass.includes('--'));
  modifers.forEach((modClass) => {
    $el.removeClass(modClass);
  });
};
/**
 * Reset modal for next ajax loaded content
 */
const resetAjaxModal = () => {
  const $ajaxModal = $('#modal');
  if ($ajaxModal.length) {
    removeModalModifiers($ajaxModal);
    $ajaxModal.find('.modal__content *:not(".modal__close, .modal__close *")').remove();
  }
};
/**
 * Ensure one modal is open at a time
 * @param  {jQuery} $modal
 * @param  {jQuery} $overlay
 */
const closeAllModals = () => {
  clearTimeout(window.modalCloseTimeout);
  resetAjaxModal();
  removeActiveClass($('.modal-overlay'), $('.modal'));
  $('body').removeClass('body--modal-overflow-hidden');
};
/**
 * Add active class on elements
 * @param  {rest}
 */
const addActiveClass = (...args) => {
  // Delay to allow for css animation
  setTimeout(()=>{
    args.forEach(($el) => {
      $el.addClass('is-active');
    });
  },1);
};
/**
 * Callback after css animation
 * @param {jQuery} $el - css animated element
 */
const afterAnimation = ($el, cb) => {
  let animationDone = false;
  $el.on('transitionend', (e) => {
    if (e.propertyName === 'transform' && !animationDone) {
      animationDone = true;
      cb();
    }
  });
  setTimeout(()=>{
    if (!animationDone) {
      animationDone = true;
      cb();
    }
  }, 500);
};
/**
 * Return to last focusable element
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const returnToLastFocus = ($content, options = {}) => {
  if (options.returnFocus) {
    options.releaseFocusFunc($content, $(options.returnFocus));
  } else {
    options.releaseFocusFunc($content, $(document).data('lastFocus'));
  }
};
/**
 * Handles the click event to close the modal
 * @param  {event} event
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const closeModal = (evt, $el, $modal, $content, $overlay, typeAdjust, options = {}) => {
  clearTimeout(window.modalCloseTimeout);
  if (evt) {
    evt.preventDefault();
  }
  if (typeAdjust.overlay) {
    removeActiveClass($overlay);
  }
  if (!typeAdjust.pageScroll) {
    $('body').removeClass('body--modal-overflow-hidden');
  }
  removeActiveClass($modal);
  options.onCleanup({ target: $el, options });
  afterAnimation($content, () => {
    options.onClose({ target: $el, options });
    if (options.url) {
      resetAjaxModal();
    }
    returnToLastFocus($content, options);
  });

  // to ensure the event isn't get bound multiple times
  $content.off('mousedown.modal');
  if (options.overlayClose) {
    // to ensure the event isn't get bound multiple times
    $modal.off('mousedown');
    $content.attr('aria-modal', false);
  }
};
/**
 * Set timeout for notification modal
 * @param  {jQuery} $el
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 */
const setNotificationDuration = ($el, $modal, $content, $overlay, typeAdjust, options) => {
  const minDuration = options.notificationDuration;

  window.modalCloseTimeout = setTimeout(() => {
    closeModal(false, $el, $modal, $content, $overlay, typeAdjust, options);
  }, minDuration);
};
/**
 * Add aria-labelledby to modal content for accessible
 * audible title that corresponds with modal's heading
 * @param  {jQuery} $content
 */
const addHedAria = ($content) => {
  let $modalHed = $($content.find('.modal__hed'));
  if (!$modalHed.length) {
    $modalHed = $content.find('.modal__focus-element');
  }
  if ($modalHed.length) {
    let modalHedID = $modalHed.attr('id') ? $modalHed.attr('id') : `modal-hed-${Math.ceil(Math.random() * 10000000000)}`;
    $content.attr('aria-labelledby', modalHedID);
    $content.attr('aria-modal', true);
    $modalHed.attr('id', modalHedID).attr('tabindex', '-1');
  }
};
/**
 * Save last focus element that's not inside modal
 * @param  {jQuery} $el - last focusable element
 */
const saveLastFocus = ($el) => {
  if (!$el.parents('.modal__content').length) {
    $(document).data('lastFocus', $el);
  }
};
/**
 * Give each <a> a unique href value, in order to avoid users hearing
 * "visited" on links they've yet to visit.
 * @param  {jQuery} $modal
 */
const uniqueCloseHref = ($modal) => {
  $modal.find('.js-modal-close').each(function eachCloseLink() {
    const $closeLink = $(this);
    if ($closeLink.is('a')) {
      $closeLink.attr('href', `#modal-uid-${Math.ceil(Math.random() * 10000000000)}`);
    }
  });
};

/**
 * Lazy loading images
 * @param {jQuery} $content
 */
const imgLazy = ($content) => {
  if ($content.find('img').length > 0 && $content.find("[data-src]").length > 0) {
    $content.find('img').each((i, el) => {
      $(el).attr('src', $(el).data('src'));
    });
  }
}

/**
 * Open modal
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const openModal = ($el, $modal, $content, $overlay, typeAdjust, options = {}) => {
  $('.modal-overlay__progress-bar').addClass('modal-overlay__progress-bar--hidden');
  options.onOpen({ target: $el, options });
  addActiveClass($modal);
  addHedAria($content);
  saveLastFocus($el);
  uniqueCloseHref($modal);
  imgLazy($content);
  $content.scrollTop(0);
  if (typeAdjust.overlay) {
    addActiveClass($overlay);
  }
  if (!typeAdjust.pageScroll) {
    $('body').addClass('body--modal-overflow-hidden');
  }
  if (!options.closeButton) {
    $content.find('.modal__close').remove();
  }
  addCloseButton($content, options);

  if (!options.video) {
    $content.removeClass('modal__content--video');
  }

  if (options.type === 'notification') {
    if ($content.find('.js-close-link--notification').length < 1 && options.closeButton) {
      $content.append(`
        <div class="js-close-link--notification u-center u-margin-t--sm">
          <button class="js-modal-close link link--light link--hover-white u-text--md" type="button">Close</button>
          <span class="u-screen-reader">Close notification modal</span>
        </div>
      `);
    }
    if (options.notificationAutoClose) {
      setNotificationDuration($el, $modal, $content, $overlay, typeAdjust, options);
    }
  }
  // Shift and trap focus to modal after animation
  afterAnimation($content, () => {
    options.trapFocusFunc($content);
    if ($content.find('.modal__hed').length) {
      $content.find('.modal__hed').focus();
    } else {
      $content.find('.modal__focus-element').focus();
    }
    options.onComplete({ target: $el, options, content: $content });
  });
};
/**
 * Handles the click event when modal trigger/ button is clicked
 * @param  {jQuery} $el - control
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {event} evt
 */
const openModalEventHandler = ($el, $modal, $content, $overlay, options = {}, evt) => {
  const typeAdjust = adjustToModalType($modal, options);

  if (evt) {
    evt.preventDefault();
  }
  closeAllModals();

  $content
    // To ensure the event isn't get bound multiple times
    .off('keydown.modalEscape')
    // escape event to close modal
    .on('keydown.modalEscape', evt => escapeModal(evt, $el, $modal, $content, $overlay, typeAdjust, options)).data('modalEscape', true)
    // Add listener to modal close button keyboard users hitting the enter key for a11y
    .on('keydown.modalClose', '.js-modal-close', event => keyboardCloseModal(event, $el, $modal, $content, $overlay, typeAdjust, options)).data('modalEscape', true)
    // Add listener modal content for modalCloseClass to close modal
    .on('mousedown.modal', '.js-modal-close', evt => closeModal(evt, $el, $modal, $content, $overlay, typeAdjust, options))
    // Prevents href getting added to end of URL
    .on('click.modal', '.js-modal-close', evt => evt.preventDefault())
    // Stop the clicks from bubbling up
    .on('mousedown.modal', evt => evt.stopPropagation());

  if (options.overlayClose) {
    $modal
      // Add listener to outside of the modal if set true
      .on('mousedown.modal', evt => closeModal(evt, $el, $modal, $content, $overlay, typeAdjust, options));
  } else {
    $modal
    // Remove listener to outside of the modal if set false
    .off('mousedown.modal');
  }

  // If url is passed then attempt to load ajax.
  if (options.url) {
    $('.modal-overlay__progress-bar').removeClass('modal-overlay__progress-bar--hidden');
    options.onLoad({ target: $el, options });
    addModalModifiers($modal, options.type);
    if (options.type !== 'notification' && options.type !== 'notification-bubble') {
      addActiveClass($overlay);
    }
    $('.modal-overlay__progress-bar').focus();
    $.ajax({
      url: options.url,
      method: options.method || 'POST',
      cache: options.cache,
      data: options.data,
      timeout: 60000,
      success: data => {
        options.onSuccess(data);
      }
    })
    .fail((jqXHR) => {
      $content
      .prepend(`<h3 class="modal__hed">${jqXHR.statusText}</h3>`);
      openModal($el, $modal, $content, $overlay, typeAdjust, options);
    })
    .done((content) => {
      $content
      .prepend(content);
      options.onPrepend();
      openModal($el, $modal, $content, $overlay, typeAdjust, options);
    });
  } else {
    openModal($el, $modal, $content, $overlay, typeAdjust, options);
  }

  // if bg needs to be transparent
  if (options.transparent) {
    $content.addClass('modal__content--bg-transparent');
  }
};
/**
 * @param  {jQuery} evt - key event
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const escapeModal = (evt, $el, $modal, $content, $overlay, typeAdjust, options) => {
  if (Keyboard.parseKey(evt) === Keyboard.keys.ESCAPE) {
    closeModal(evt, $el, $modal, $content, $overlay, typeAdjust, options);
  }
};
/**
 * @param  {jQuery} evt - key event
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
 const keyboardCloseModal = (evt, $el, $modal, $content, $overlay, typeAdjust, options) => {
  if (Keyboard.parseKey(evt) === Keyboard.keys.ENTER) {
    closeModal(evt, $el, $modal, $content, $overlay, typeAdjust, options);
  }
};
/**
 * Applies any sort of event handlers for the modal.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setEventListeners = ($el, $modal, $content, $overlay, options = {}) => {
  // Add escape key down feature
  // Add listener to button to open modal
  // Track mouse movement to prevent click on slick swipe
  if (!options.triggerOpen) {
    if(options.triggerByClick) {
      $($el)
      .on('click.modal', evt => openModalEventHandler($el, $modal, $content, $overlay, options, evt));
    } else {
      let isDragging = false;
      $($el).on('mousedown.modal', function() {
        isDragging = false;
        $($el).on('mousemove.modal', function() {
          isDragging = true;
          $($el).off("mousemove.modal");
        })
      })
      $($el).on('mouseup.modal', function(evt) {
        if (!isDragging) {
          openModalEventHandler($el, $modal, $content, $overlay, options, evt)
        }
      })
    }
  }

  if ($content[0] && (options.type === 'notification')) {
    options.loadHammerJS().then((Hammer) => {
      const hammertime = new Hammer($content[0]);
      hammertime.get('swipe').set({ direction: Hammer.DIRECTION_VERTICAL });
      hammertime.on('swipeup', evt => closeModal(evt, $el, $modal, $content, $overlay, options));
    });
  }
};
/**
 * Remove any sort of event handlers for the modal.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 */
const removeEventListeners = ($el, $modal, $content) => {
  $el.off('click.modal');
  $content
    .off('mousedown.modal')
    .off('keydown.modalClose')
    .off('keydown.modalEscape');
  $modal.off('mousedown.modal');
};
/**
 * If triggerOpen option is set true open modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const checkTriggerOpen = ($el, $modal, $content, $overlay, options = {}) => {
  if (options.triggerOpen) {
    openModalEventHandler($el, $modal, $content, $overlay, options, false);
  }
};
/**
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {Function} deregister - teardown method from register jquery plugin
 */
const destroy = ($el, $modal, $content, options = {}, deregister) => {
  removeEventListeners($el, $modal, $content);
  removeActiveClass($('.modal-overlay'), $('.modal'));
  removeModalModifiers($modal);
  deregister();
  $('body').removeClass('body--modal-overflow-hidden');
  if (options.url) {
    $modal.remove();
  }
};
/* Add Close Button to modal
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const addCloseButton = ($content, options = {}) => {
  if (options.closeButton
    && !$content.find('.modal__close').length
    && options.type !== 'notification') {
    $content.prepend(`
      <button class="js-modal-close modal__close modal__close--notification" type="button">
        <span class="icon icon--x">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
          <path d="M9.06 8l2.72-2.72A.75.75 0 0 0 10.72 4.22L8 6.94 5.28 4.22A.75.75 0 0 0 4.22 5.28L6.94 8l-2.72 2.72A.75.75 0 1 0 5.28 11.78L8 9.06l2.72 2.72a.75.75 0 0 0 1.061-1.061z"/>
        </svg>
        <div class="u-screen-reader">Close Modal</div>
        </span>
      </button>
    `);
  }
};
const setA11y = ($el, opts) => {
  setA11yAttr($el)
  if (!$el.find(".u-screen-reader").length) {
    $el.append(` <span class="u-screen-reader">${opts.srLinkText}</span>`);
  }
  $el.data('setA11yOnly', false);
};
/**
 * Initializes modal.
 * @param {HTMLElement} el - button /element that opens modal
 * @param {Object} options - Options used for wiring up the modal
 * @param {Object} deregister - Performs a deregisteration on the plugin
 * @see DEFAULTS
 */
export default function modal(el, options = {}, { deregister } = noopActions) {
  const $el = options.triggerElement ? $(options.triggerElement) : $(el);
  const opts = mergeOptions($el, options);
  if (opts.setA11yOnly) {
    setA11y($el, opts);
  }
  if (opts && !opts.setA11yOnly) {
    const $modal = setModal($el, opts);
    const $overlay = appendModalOverlay(opts);
    const $content = wrapModalContent($modal, opts);
    setA11y($el, opts);
    if (opts.showProgressBar) {
      appendLoadingBar($overlay);
    }
    setEventListeners($el, $modal, $content, $overlay, opts, deregister);
    checkTriggerOpen($el, $modal, $content, $overlay, opts, deregister);
    return {
      destroy: () => destroy($el, $modal, $content, opts, deregister),
    };
  }
  return true;
}
/**
 * Utility Function for Modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
$.modal = (options) => {
  $('<div/>').modal(options);
};
/**
 * Utility Function for Closing Modal
 */
$.modal.close = () => {
  closeAllModals();
};
registerJQueryPlugin(PLUGIN_NAME, modal);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/modal.js